// Shared default for every "Date Range" filter in the dashboard.
//
// Filters must not start blank — blank silently means "all records ever" and
// the user cannot tell what period the numbers on screen cover. They open
// on the firm's official training year in FULL — configured start through the
// day before the next start — so every L&D tab answers the same question the
// Official Training Report does. Because the range is real ISO text it shows
// up in the <input type="date"> boxes themselves. Clearing the inputs still
// means "no bound", so all-time remains reachable.
//
// The end is NOT *today* (a period-to-date view): the
// default must be the whole training year, so anything already scheduled later
// in the year is inside the default window rather than invisible until its
// date passes.
//
// The training year's start is CONFIGURED (Settings → L&D Training, single-row
// ld_training_settings; default 1 October) rather than hard-coded.
// This module keeps a cached copy: call loadLdSettings() before
// the first getDefaultDateRange() — LDDashboard gates its tabs on it — and the
// sync helpers below read the cache (falling back to the 10/1 + 40h defaults
// when the fetch has not happened or failed).

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const pad = (n) => String(n).padStart(2, '0');

/** Local calendar day as ISO 'YYYY-MM-DD' (never UTC — no off-by-one day). */
export function toIsoDate(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Today on the firm's calendar (Singapore) as ISO 'YYYY-MM-DD', whatever the
 * browser's time zone — the same day the server's firmToday() checks against.
 */
export function firmToday(now = new Date()) {
  // en-CA formats as YYYY-MM-DD.
  return now.toLocaleDateString('en-CA', { timeZone: 'Asia/Singapore' });
}

// Cached settings — the defaults double as the offline fallback.
let ldSettings = {
  mandatoryHoursTarget: 40,
  periodStartMonth: 10,
  periodStartDay: 1,
};
let loadPromise = null;

/**
 * Fetch-and-cache the L&D settings (one request per page load; concurrent
 * callers share the promise). Never rejects — a failed fetch keeps the
 * defaults so the dashboard still renders.
 */
export function loadLdSettings() {
  if (!loadPromise) {
    loadPromise = fetch(`${API_BASE_URL}/api/ld/settings`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.settings) {
          ldSettings = {
            mandatoryHoursTarget: Number(data.settings.mandatoryHoursTarget) || 40,
            periodStartMonth: Number(data.settings.periodStartMonth) || 10,
            periodStartDay: Number(data.settings.periodStartDay) || 1,
          };
        }
        return ldSettings;
      })
      .catch(() => ldSettings);
  }
  return loadPromise;
}

/** The cached settings (defaults until loadLdSettings() resolves). */
export function getLdSettings() {
  return ldSettings;
}

/** Start of the current training year, as ISO 'YYYY-MM-DD'. */
export function officialPeriodStart(today = new Date()) {
  const { periodStartMonth: m, periodStartDay: d } = ldSettings;
  const startThisYear = new Date(today.getFullYear(), m - 1, d);
  const year = today >= startThisYear ? today.getFullYear() : today.getFullYear() - 1;
  return `${year}-${pad(m)}-${pad(d)}`;
}

/**
 * End of the current training year = the day before the next start
 * (one knob, no configurable gap/overlap), e.g. 10/1 → 9/30.
 */
export function officialPeriodEnd(today = new Date()) {
  const [y, m, d] = officialPeriodStart(today).split('-').map((v) => parseInt(v, 10));
  const end = new Date(y + 1, m - 1, d);
  end.setDate(end.getDate() - 1);
  return toIsoDate(end);
}

const monthLabel = (iso) => {
  const [y, m] = iso.split('-').map((v) => parseInt(v, 10));
  const monthName = new Date(2000, m - 1, 1).toLocaleString('en-US', { month: 'short' });
  return `${monthName} ${y}`;
};

/** Both ends of the training year, e.g. 'Oct 2025 - Sep 2026' — Reset button text. */
export function officialPeriodRangeLabel(today = new Date()) {
  return `${monthLabel(officialPeriodStart(today))} - ${monthLabel(officialPeriodEnd(today))}`;
}

/** The default filter window: the whole official training year, inclusive. */
export function getDefaultDateRange(today = new Date()) {
  return { startDate: officialPeriodStart(today), endDate: officialPeriodEnd(today) };
}
